import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { PARTNER_CATEGORIES, PARTNER_STATUSES, PARTNER_PRIORITIES, PROSPECT_SOURCES, pl, statusEntry, sourceEntry, actionEntry } from "../../lib/partnerConstants";
import PartnerDetailDialog from "./PartnerDetailDialog";
import { Handshake, Star, Clock, AlertTriangle, Award, Users, Activity, TrendingUp, Zap } from "lucide-react";

const Kpi = ({ icon: Icon, label, value, color, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide"><Icon className="h-4 w-4" style={{ color }} /> {label}</div>
    <div className="text-2xl font-bold mt-1" style={{ color }}>{value}</div>
  </div>
);

export default function PriorityBoard({ readOnly }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "fr";
  const [data, setData] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/crm/partners/priority-board"); setData(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-16 text-center text-muted-foreground">…</div>;
  const a = data.analytics || {};
  const board = data.board || {};

  const srcRow = (obj) => {
    const entries = Object.entries(obj || {}).sort((x, y) => y[1] - x[1]).slice(0, 6);
    if (!entries.length) return <span className="text-muted-foreground text-xs">—</span>;
    return entries.map(([k, v]) => {
      const e = sourceEntry(k);
      return <div key={k} className="flex justify-between text-xs py-0.5"><span className="text-foreground/70">{e ? pl(lang, e) : k}</span><span className="font-semibold">{v}</span></div>;
    });
  };

  return (
    <div className="space-y-5" data-testid="crm-priority-board">
      {/* Analytics KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Users} label={t("crm.priority.met")} value={a.met ?? 0} color="#38bdf8" testid="kpi-met" />
        <Kpi icon={Activity} label={t("crm.priority.active")} value={a.active ?? 0} color="#10b981" testid="kpi-active" />
        <Kpi icon={AlertTriangle} label={t("crm.priority.inactive")} value={a.inactive ?? 0} color="#f43f5e" testid="kpi-inactive" />
        <Kpi icon={TrendingUp} label={t("crm.priority.conversion")} value={`${a.conversion ?? 0}%`} color="#fbbf24" testid="kpi-conversion" />
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
          <div className="text-sm font-semibold flex items-center gap-2 mb-2"><Zap className="h-4 w-4 text-amber-400" /> {t("crm.priority.topPrescripteurs")}</div>
          <div className="space-y-1" data-testid="top-prescripteurs">
            {(a.top_prescripteurs || []).length ? a.top_prescripteurs.map((p) => (
              <div key={p.id} className="flex justify-between text-xs py-0.5 cursor-pointer hover:text-emerald-400" onClick={() => setDetailId(p.id)}><span className="truncate">{p.name}</span><span className="font-semibold text-amber-300">{p.admissions} {t("crm.priority.adm")}</span></div>
            )) : <span className="text-muted-foreground text-xs">—</span>}
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
          <div className="text-sm font-semibold mb-2">{t("crm.priority.bySourceReq")}</div>
          <div data-testid="by-source-req">{srcRow(a.by_source_requests)}</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
          <div className="text-sm font-semibold mb-2">{t("crm.priority.bySourceAdm")}</div>
          <div data-testid="by-source-adm">{srcRow(a.by_source_admissions)}</div>
        </div>
      </div>

      {/* Board 30 prioritaires */}
      <div>
        <h3 className="font-display font-bold text-lg">{t("crm.priority.title30")}</h3>
        <p className="text-xs text-muted-foreground mb-3">{t("crm.priority.subtitle")}</p>
        <div className="grid lg:grid-cols-3 gap-3">
          {["medical", "institutionnel", "associatif"].map((cat) => (
            <div key={cat} className="rounded-2xl border border-white/10 bg-white/[0.02] p-3" data-testid={`board-col-${cat}`}>
              <div className="text-sm font-bold mb-2 flex items-center gap-2" style={{ color: PARTNER_CATEGORIES[cat].color }}>
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: PARTNER_CATEGORIES[cat].color }} /> {pl(lang, PARTNER_CATEGORIES[cat])}
                <span className="ml-auto text-xs text-muted-foreground font-normal">{(board[cat] || []).length}/10</span>
              </div>
              <div className="space-y-2">
                {(board[cat] || []).map((p) => {
                  const st = statusEntry(p.status);
                  const prio = PARTNER_PRIORITIES[p.priority];
                  return (
                    <div key={p.id} className="rounded-xl border border-white/10 p-2.5 cursor-pointer hover:border-emerald-400/30 transition" onClick={() => setDetailId(p.id)} data-testid={`board-partner-${p.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm truncate flex items-center gap-1"><Handshake className="h-3.5 w-3.5 text-emerald-400 shrink-0" /> {p.name} {p.strategic && <Star className="h-3 w-3 text-amber-400 shrink-0" />}</span>
                        {prio && <span className="shrink-0 rounded-full px-1.5 text-[10px] font-bold" style={{ color: prio.color, background: prio.color + "22" }}>{p.priority}</span>}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {st && <span className="rounded-full px-1.5 py-0.5 text-[9px] font-semibold" style={{ color: st.color, background: st.color + "22" }}>{pl(lang, st)}</span>}
                        {p.inactive && <span className="rounded-full px-1.5 py-0.5 text-[9px] font-semibold bg-rose-500/20 text-rose-300 flex items-center gap-0.5" data-testid={`board-inactive-${p.id}`}><AlertTriangle className="h-2.5 w-2.5" /> {t("crm.priority.inactiveAlert")}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-foreground/70">
                        <span title={t("crm.partner.orientations")}><Award className="h-3 w-3 inline mr-0.5 text-sky-400" />{p.orientations}</span>
                        <span title={t("crm.partner.admissions")}>{p.admissions} {t("crm.priority.adm")}</span>
                        <span title={t("crm.priority.conversion")} className="text-amber-300">{t("crm.priority.conv")} {p.conversion}%</span>
                      </div>
                      {p.relance_date && (
                        <div className="text-[10px] mt-1 flex items-center gap-1 text-amber-400"><Clock className="h-2.5 w-2.5" /> {(actionEntry(p.next_action) ? pl(lang, actionEntry(p.next_action)) : p.next_action) || "—"} · {p.relance_date}</div>
                      )}
                    </div>
                  );
                })}
                {!(board[cat] || []).length && <p className="text-xs text-muted-foreground py-3 text-center">{t("crm.priority.noPartners")}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {detailId && <PartnerDetailDialog partnerId={detailId} open={!!detailId} onClose={() => setDetailId(null)} onChanged={load} relationEditable={data.relation_editable} readOnly={readOnly} />}
    </div>
  );
}
